import React from 'react';
import { usePortfolio } from '../../context/PortfolioContext';
import { HelpCircle, Terminal as TerminalIcon } from 'lucide-react';

const MacMenuBar = ({ currentSection, onSectionChange, onTerminalToggle, isTerminalOpen, onWindowAction }) => {
  const { isAdminMode } = usePortfolio();

  // Curated navigation - Skills & Contact removed, Help moved to right logo
  const baseMenu = [
    { label: 'Dashboard', section: 'dashboard' },
    { label: 'About', section: 'about' },
    { label: 'Projects', section: 'projects' },
    { label: 'Experience', section: 'experience' },
    { label: 'Education', section: 'education' },
    { label: 'Resume', section: 'resume' },
  ];
  
  // Admin entry shows only when user is authenticated as admin
  const menuItems = isAdminMode ? [...baseMenu, { label: 'Admin', section: 'admin' }] : baseMenu;

  const handleMenuClick = (item) => {
    if (item.section) {
      onSectionChange(item.section);
    }
  };

  return (
    <header className="sticky top-0 z-[70] w-full bg-[#09090b]/95 backdrop-blur-2xl border-b border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.9)]">
      <div className="w-full px-3 sm:px-6 py-2.5 flex items-center justify-between gap-3">
        {/* Left side - Navigation Sections */}
        <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto no-scrollbar py-0.5 min-w-0">
          {/* Navigation Items */}
          {menuItems.map((item) => {
            const isActive = currentSection === item.section;
            return (
              <button
                key={item.label}
                onClick={() => handleMenuClick(item)}
                className={`relative px-3 py-1.5 font-mono text-xs sm:text-sm transition-all duration-150 shrink-0 ${
                  isActive
                    ? 'text-white font-semibold'
                    : 'text-zinc-400 hover:text-white hover:bg-white/5 rounded-lg font-medium'
                }`}
              >
                <span>{item.label}</span>
                {isActive && (
                  <span className="absolute bottom-0 left-2 right-2 h-[2px] bg-white rounded-full shadow-[0_0_8px_rgba(255,255,255,0.7)]" />
                )}
              </button>
            );
          })}
        </div>

        {/* Right side - Repositioned Help Logo + Terminal Quick Access + Social Icons */}
        <div className="flex items-center space-x-1.5 sm:space-x-2 shrink-0 ml-1">
          {/* Repositioned Help Logo Button */}
          <button
            onClick={() => onSectionChange(currentSection === 'help' ? 'dashboard' : 'help')}
            className={`p-1.5 sm:p-2 rounded-xl transition-all flex items-center justify-center ${
              currentSection === 'help'
                ? 'bg-white/15 text-white border border-white/40 shadow-[0_0_12px_rgba(255,255,255,0.15)]'
                : 'bg-white/5 border border-white/15 text-zinc-300 hover:text-white hover:border-white/35 hover:bg-white/10'
            }`}
            title="Help & Terminal Commands (?)"
            aria-label="Help"
          >
            <HelpCircle className="w-4 h-4" />
          </button>

          {/* Terminal Toggle Button */}
          {onTerminalToggle && (
            <button
              onClick={onTerminalToggle}
              className={`p-1.5 sm:p-2 rounded-xl transition-all flex items-center justify-center ${
                isTerminalOpen
                  ? 'bg-white/15 text-white border border-white/40 shadow-[0_0_12px_rgba(255,255,255,0.15)]'
                  : 'bg-white/5 border border-white/15 text-zinc-300 hover:text-white hover:border-white/35 hover:bg-white/10'
              }`}
              title="Terminal (⌘T)"
              aria-label="Toggle Terminal"
            >
              <TerminalIcon className="w-4 h-4" />
            </button>
          )}

          <div className="w-px h-4 bg-white/15 mx-0.5 hidden sm:block" />

          {/* Social Links */}
          <a 
            href="https://github.com/shubhhh19" 
            target="_blank" 
            rel="noreferrer" 
            className="p-1.5 sm:p-2 rounded-xl bg-white/5 border border-white/15 text-zinc-300 hover:text-white hover:border-white/40 hover:bg-white/10 transition-all" 
            title="GitHub"
          >
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
          </a>
          <a 
            href="https://linkedin.com/in/shubhsoni" 
            target="_blank" 
            rel="noreferrer" 
            className="p-1.5 sm:p-2 rounded-xl bg-white/5 border border-white/15 text-zinc-300 hover:text-white hover:border-white/40 hover:bg-white/10 transition-all" 
            title="LinkedIn"
          >
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
          <a 
            href="mailto:sonishubh2004@gmail.com" 
            className="p-1.5 sm:p-2 rounded-xl bg-white/5 border border-white/15 text-zinc-300 hover:text-white hover:border-white/40 hover:bg-white/10 transition-all" 
            title="Email"
          >
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M0 3v18h24V3H0zm21.518 2L12 12.713 2.482 5h19.036zM2 19V7.183l10 8.104 10-8.104V19H2z"/>
            </svg>
          </a>
        </div>
      </div>
    </header>
  );
};

export default MacMenuBar;